import { useState, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { useTranslation } from 'react-i18next';
import styles from './WidgetStyles.module.css';
import { ReactionBar } from './ReactionBar';
import { FeedbackDialog } from './FeedbackDialog';
import type { AssistantImageMap } from '../types/chat';
import type { SubmitFeedbackInput } from '../types/feedback';
import { getAssistantImage } from '../utils/assistantImageCache';

type AssistantMessageCardProps = {
    content: string;
    isPending?: boolean;
    statusMessage?: string;
    onCopy?: (content: string) => void | Promise<void>;
    durationMs?: number;
    showDuration?: boolean;
    images?: AssistantImageMap;
    onImagePreviewRequested?: (preview: AssistantImagePreview) => void;
    onImageLoad?: () => void;
    showFeedback?: boolean;
    isSubmittingFeedback?: boolean;
    onSubmitFeedback?: (input: SubmitFeedbackInput) => Promise<boolean>;
};

export type AssistantImagePreview = {
    src: string;
    alt: string;
};

export function AssistantMessageCard({
    content,
    isPending = false,
    statusMessage,
    onCopy,
    durationMs,
    showDuration = true,
    images = {},
    onImagePreviewRequested,
    onImageLoad,
    showFeedback = false,
    isSubmittingFeedback = false,
    onSubmitFeedback
}: AssistantMessageCardProps) {
    const { t } = useTranslation();
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');
    const showSpinner = isPending && (!statusMessage || !statusMessage.length) && (!content || !content.trim().length);
    const showStatus = Boolean(statusMessage) && (!content || !content.trim().length);

    const showContent = !showSpinner && !showStatus;
    const durationLabel = showDuration && showContent && !isPending ? formatDurationLabel(durationMs, t) : null;

    const submitPositiveFeedback = async () => {
        if (!onSubmitFeedback) {
            return;
        }

        setFeedbackError('');
        const success = await onSubmitFeedback({ type: 'positive' });
        if (!success) {
            setFeedbackError(t('feedbackSubmitError'));
        }
    };

    const submitNegativeFeedback = async (reason: SubmitFeedbackInput['reason'], comment: string) => {
        if (!onSubmitFeedback || !reason) {
            return;
        }

        setFeedbackError('');
        const success = await onSubmitFeedback({
            type: 'negative',
            reason,
            comment
        });

        if (success) {
            setIsFeedbackDialogOpen(false);
            return;
        }

        setFeedbackError(t('feedbackSubmitError'));
    };

    return (
        <li className={`${styles.messageCard} ${styles.assistantMessageCard}`}>
            {showSpinner ? (
                <div className={styles.typingIndicator} aria-live="polite" aria-label="Assistant is responding">
                    <span />
                    <span />
                    <span />
                </div>
            ) : showStatus ? (
                <div className={styles.statusMessage} aria-live="polite">
                    <span className={styles.statusMessageBar} aria-hidden="true" />
                    <p>{statusMessage}</p>
                </div>
            ) : (
                <div className={styles.assistantMessageMarkdown}>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                        components={{
                            p: ({ children }) => <p className={styles.assistantMessageBody}>{children}</p>,
                            pre: ({ children }) => <pre className={styles.assistantMessagePreformatted}>{children}</pre>,
                            code: ({ children, className, ...props }) => (
                                <code
                                    {...props}
                                    className={[className, styles.assistantMessageCode].filter(Boolean).join(' ')}
                                >
                                    {children}
                                </code>
                            ),
                            table: ({ children }) => (
                                <div className={styles.assistantMessageTableScroller} data-widget-table-scroller="true">
                                    <table className={styles.assistantMessageTable} data-widget-table="true">
                                        {children}
                                    </table>
                                </div>
                            ),
                            th: ({ children }) => <th className={styles.assistantMessageTableHeader}>{children}</th>,
                            td: ({ children }) => <td className={styles.assistantMessageTableCell}>{children}</td>,
                            a: ({ children, ...props }) => (
                                <a
                                    {...props}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className={styles.assistantMessageLink}
                                >
                                    {children}
                                </a>
                            ),
                            img: ({ src, alt, ...props }) => {
                                const resolved = resolveInlineImageSource(src ?? '', images);
                                const resolvedSrc = resolved?.src ?? src;
                                const resolvedAlt = alt ?? resolved?.hint ?? '';
                                const canPreview = typeof resolvedSrc === 'string' && resolvedSrc.length > 0;
                                const handlePreview = () => {
                                    if (!canPreview || !onImagePreviewRequested) {
                                        return;
                                    }

                                    onImagePreviewRequested({
                                        src: resolvedSrc,
                                        alt: resolvedAlt
                                    });
                                };
                                const handleKeyDown = (event: KeyboardEvent<HTMLImageElement>) => {
                                    if (event.key !== 'Enter' && event.key !== ' ') {
                                        return;
                                    }

                                    event.preventDefault();
                                    handlePreview();
                                };

                                // eslint-disable-next-line jsx-a11y/alt-text
                                return (
                                    <img
                                        className={styles.assistantMessageImage}
                                        src={resolvedSrc}
                                        alt={resolvedAlt}
                                        data-image-id={resolved?.id}
                                        role={canPreview ? 'button' : undefined}
                                        tabIndex={canPreview ? 0 : undefined}
                                        onClick={handlePreview}
                                        onKeyDown={handleKeyDown}
                                        onLoad={onImageLoad}
                                        {...props}
                                    />
                                );
                            }
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                </div>
            )}
            {showContent ? (
                <ReactionBar
                    onCopy={content ? () => onCopy?.(content) : undefined}
                    durationLabel={durationLabel}
                    showFeedback={showFeedback}
                    onThumbUp={submitPositiveFeedback}
                    onThumbDown={() => setIsFeedbackDialogOpen(true)}
                    isSubmittingFeedback={isSubmittingFeedback}
                />
            ) : null}
            <FeedbackDialog
                isOpen={isFeedbackDialogOpen}
                onClose={() => setIsFeedbackDialogOpen(false)}
                onSubmit={submitNegativeFeedback}
                isSubmitting={isSubmittingFeedback}
                errorMessage={feedbackError}
            />
        </li>
    );
}

function formatDurationLabel(
    durationMs: number | undefined,
    translate: (key: string, options?: Record<string, unknown>) => string
): string | null {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
        return null;
    }

    const seconds = Math.max(0, Math.round(durationMs / 1000));
    const formattedSeconds = seconds.toString();
    const template = translate('responseDuration', { seconds: formattedSeconds });

    if (!template.length) {
        return `Duration ${formattedSeconds} (s)`;
    }

    return template;
}

const Base64Prefix = 'base64/';

function resolveInlineImageSource(src: string, images: AssistantImageMap) {
    if (typeof src !== 'string' || !src.startsWith(Base64Prefix)) {
        return null;
    }

    const afterPrefix = src.slice(Base64Prefix.length);
    if (!afterPrefix.length) {
        return null;
    }

    const terminatorIndex = findTerminator(afterPrefix);
    const imageId = terminatorIndex >= 0 ? afterPrefix.slice(0, terminatorIndex) : afterPrefix;

    if (!imageId) {
        return null;
    }

    const resolved = images[imageId] ?? getAssistantImage(imageId);
    if (!resolved || !resolved.base64) {
        return null;
    }

    const mime = resolved.mimeType?.trim() || 'image/png';
    const dataUrl = `data:${mime};base64,${resolved.base64}`;

    return {
        id: imageId,
        src: dataUrl,
        hint: resolved.hint
    };
}

function findTerminator(value: string): number {
    const terminators = ['.', '/', '?', '#'];
    let index = -1;

    for (const terminator of terminators) {
        const position = value.indexOf(terminator);
        if (position >= 0 && (index === -1 || position < index)) {
            index = position;
        }
    }

    return index;
}
